import React, { useEffect } from 'react';
import { Box, Button, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import EditIcon from '@mui/icons-material/Edit';
import APIRequest, { APIError } from 'system/APIRequest';
import { enqueueSnackbar } from 'notistack';
import { defaultQuery, Query } from 'system/query/Query';
import SavedQueryRow from './SavedQueryRow';
import { Folder } from '@mui/icons-material';
import SavedQueryDialog from './SavedQueryDialog';
import QueryDialog from '../QueryDialog';

export interface SavedQuerySelectionProps {
  datasetId?: string;
  selectedQueryId: string;
  setSelectedQueryId: (queryId: string | undefined) => void;
  // Called when the selected query cannot be loaded (e.g., it was deleted)
  onMissing?: () => void;

  size?: "small" | "medium" | "large";
}

export default function SavedQuerySelection(props: SavedQuerySelectionProps): JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();

  const [savedQueryDialogIsOpen, setSavedQueryDialogIsOpen] = useState<boolean>(false);
  const [queryEditDialogIsOpen, setQueryDialogIsOpen] = useState<boolean>(false);

  const [selectedQuery, setSelectedQuery] = useState<Query | undefined>(undefined);

  // Load the selected dashboard from the selectedDashboardId
  useEffect(() => {
    if (!props.selectedQueryId) {
      setSelectedQuery(undefined);
      return;
    }
    APIRequest(`obj/query/${props.selectedQueryId}`).then((response: any) => {
      setSelectedQuery(response.output);
    }).catch((reason: APIError) => {
      props.setSelectedQueryId(undefined);
      if (props.onMissing) {
        props.onMissing();
      } else {
        enqueueSnackbar(`${i18n.t('query-form.error-loading-query')}: ${reason.message}`, {variant: 'error', persist: true});
      }
    });
  }, [props.selectedQueryId]);

  const starterQuery = new Query({...defaultQuery});
  if (props.datasetId) {
    defaultQuery.dataset_id = props.datasetId;
  }

  return (
  <>
    {selectedQuery &&
      <Box sx={{ mb: 1 }}>
        <SavedQueryRow
          savedQuery={selectedQuery}
          loadOwnQueryInfo={true}
          onClick={() => {
            setQueryDialogIsOpen(true);
          }} />
      </Box>
    }
    <Button
      variant={selectedQuery ? "outlined" : "contained"}
      onClick={() => setSavedQueryDialogIsOpen(true)}
      size={"small"}
      endIcon={<Folder />}>
      {i18n.t('query-form.load-saved-query')}
    </Button>
    <Button
      variant={selectedQuery ? "outlined" : "contained"}
      onClick={() => setQueryDialogIsOpen(true)}
      size={"small"}
      sx={{ ml: 1 }}
      endIcon={<EditIcon />}>
      {selectedQuery ? i18n.t('query-form.edit-query') : i18n.t('query-form.create-query')}
    </Button>
    {selectedQuery &&
    <>
      <Button
        color="error"
        onClick={() => { props.setSelectedQueryId(undefined); }}
        size={"small"}
        sx={{ ml: 1 }}>
        {i18n.t('query-form.clear-query')}
      </Button>
    </>
    }
    {savedQueryDialogIsOpen &&
      <SavedQueryDialog
        fixedDatasetId={props.datasetId}
        isOpen={savedQueryDialogIsOpen}
        onSubmit={(queries:Query[]) => {
          props.setSelectedQueryId(queries?.[0]?.id || "");
          setSavedQueryDialogIsOpen(false);
        }}
        onClose={() => {
          setSavedQueryDialogIsOpen(false);
        }}
      />
    }
    {queryEditDialogIsOpen &&
      <QueryDialog
        fixedDatasetId={props.datasetId}
        isOpen={queryEditDialogIsOpen}
        query={selectedQuery || starterQuery}
        onClose={() => {
          setQueryDialogIsOpen(false);
        }}
        onSubmit={(queryId:string) => {
          props.setSelectedQueryId(queryId);
          APIRequest(`obj/query/${queryId}`).then((response: any) => {
            setSelectedQuery(response.output);
          });
          setQueryDialogIsOpen(false);
        }} />
      }

  </>
  );
}